import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = ({ show, onHide, isPage = false }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (isPage) {
      navigate(-1);
    } else {
      onHide();
    }
  };

  const content = (
    <>
      <div className="text-center mb-4">
        <h4 className="text-warning">Taxi-Local.uk Ltd</h4>
        <p className="text-muted">Cookie Policy</p>
      </div>
      
      <div className="mb-4">
        <p className="text-muted small">
          <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB')}
        </p>
        <p>
          This Cookie Policy explains how Taxi-Local.uk Ltd ("we", "us", or "our") uses cookies 
          and similar tracking technologies on our website and platform. It explains what these 
          technologies are, why we use them, and your rights to control our use of them.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>1. What Are Cookies?</h5>
        <p>
          Cookies are small text files that are placed on your device (computer, smartphone, or tablet) 
          when you visit a website. They are widely used to make websites work more efficiently and to 
          provide information to website owners.
        </p>
        <p>
          Cookies can be "session" cookies (which are deleted when you close your browser) or "persistent" 
          cookies (which remain on your device for a set period or until you delete them).
        </p>
      </div>
      
      <div className="mb-4">
        <h5>2. How We Use Cookies</h5>
        <p>We use cookies for the following purposes:</p>
        
        <h6>2.1 Essential Cookies</h6>
        <p>
          These cookies are necessary for the website to function properly. They enable core functionality 
          such as security, network management, and accessibility. You cannot opt-out of these cookies 
          without affecting website functionality.
        </p>
        <ul>
          <li><strong>Authentication:</strong> To keep you logged in and maintain your session</li>
          <li><strong>Security:</strong> To protect against fraud and unauthorized access</li>
          <li><strong>Load Balancing:</strong> To distribute website traffic across servers</li>
          <li><strong>Preferences:</strong> To remember your preferences and settings</li>
        </ul>
        
        <h6>2.2 Functionality Cookies</h6>
        <p>
          These cookies enable enhanced functionality and personalization. They may be set by us or by 
          third-party providers whose services we use.
        </p>
        <ul>
          <li><strong>User Preferences:</strong> To remember your language, region, and display preferences</li>
          <li><strong>Location Services:</strong> To provide location-based features (with your permission)</li>
          <li><strong>Form Data:</strong> To remember information you've entered in forms</li>
        </ul>
        
        <h6>2.3 Analytics Cookies</h6>
        <p>
          These cookies help us understand how visitors interact with our website by collecting and 
          reporting information anonymously.
        </p>
        <ul>
          <li><strong>Usage Statistics:</strong> To analyze how our website is used and improve user experience</li>
          <li><strong>Performance:</strong> To monitor website performance and identify issues</li>
          <li><strong>User Behavior:</strong> To understand user patterns and optimize our platform</li>
        </ul>
        
        <h6>2.4 Marketing Cookies</h6>
        <p>
          These cookies are used to track visitors across websites to display relevant advertisements. 
          They may be used by advertising networks to build a profile of your interests.
        </p>
        <ul>
          <li><strong>Advertising:</strong> To deliver relevant advertisements (if we use advertising services)</li>
          <li><strong>Remarketing:</strong> To show you relevant content based on your previous visits</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>3. Third-Party Cookies</h5>
        <p>We use services from third-party providers that may set their own cookies:</p>
        
        <h6>3.1 Google Maps</h6>
        <p>
          We use Google Maps to provide mapping and location services. Google may set cookies to 
          enable map functionality, location services, and to improve their services. For more 
          information, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
        </p>
        
        <h6>3.2 Payment Processors</h6>
        <p>
          When processing payments, our payment service providers (such as GoCardless) may set cookies 
          to ensure secure transactions and prevent fraud.
        </p>
        
        <h6>3.3 Analytics Services</h6>
        <p>
          We may use analytics services to understand how our platform is used. These services may 
          use cookies to collect anonymous usage data.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>4. Managing Cookies</h5>
        <p>You have several options for managing cookies:</p>
        
        <h6>4.1 Browser Settings</h6>
        <p>
          Most web browsers allow you to control cookies through their settings. You can set your 
          browser to refuse cookies, delete cookies, or alert you when cookies are being sent. 
          However, disabling cookies may affect the functionality of our platform.
        </p>
        <p>Instructions for common browsers:</p>
        <ul>
          <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
          <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
          <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions</li>
        </ul>
        
        <h6>4.2 Cookie Consent</h6>
        <p>
          When you first visit our website, you may see a cookie consent banner. You can accept or 
          reject non-essential cookies through this banner. You can change your preferences at any time.
        </p>
        
        <h6>4.3 Opt-Out Tools</h6>
        <p>
          You can use the following tools to opt out of certain cookies:
        </p>
        <ul>
          <li><a href="https://www.youronlinechoices.com/uk/" target="_blank" rel="noopener noreferrer">Your Online Choices</a> - For advertising cookies</li>
          <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a> - For Google Analytics cookies</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>5. Types of Cookies We Use</h5>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Type</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>session_token</td>
                <td>Maintains user login session</td>
                <td>Essential</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>user_preferences</td>
                <td>Stores user preferences and settings</td>
                <td>Functionality</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>location_consent</td>
                <td>Remembers location permission choice</td>
                <td>Functionality</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>cookie_consent</td>
                <td>Remembers cookie consent preferences</td>
                <td>Essential</td>
                <td>1 year</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-muted small">
          Note: Third-party services (Google Maps, payment processors) may set additional cookies 
          not listed here. Please refer to their respective privacy policies.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>6. Impact of Disabling Cookies</h5>
        <p>
          If you choose to disable cookies, some features of our platform may not work properly:
        </p>
        <ul>
          <li>You may need to log in more frequently</li>
          <li>Some personalized features may not be available</li>
          <li>Location-based services may not function correctly</li>
          <li>Your preferences may not be saved</li>
          <li>Some forms may not work as expected</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>7. Updates to This Cookie Policy</h5>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in our practices or 
          for other operational, legal, or regulatory reasons. We will notify you of any significant 
          changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>8. More Information</h5>
        <p>
          For more information about cookies and how they work, you can visit:
        </p>
        <ul>
          <li><a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">All About Cookies</a></li>
          <li><a href="https://www.aboutcookies.org/" target="_blank" rel="noopener noreferrer">About Cookies</a></li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>9. Contact Us</h5>
        <p>
          If you have any questions about our use of cookies, please contact us:
        </p>
        <p>
          <strong>Taxi-Local.uk Ltd</strong><br/>
          Email: privacy@taxi-local.uk<br/>
          Website: www.taxi-local.uk
        </p>
      </div>
      
      <div className="text-muted small">
        <p>This Cookie Policy is governed by English law and subject to the jurisdiction of English courts.</p>
      </div>
    </>
  );

  if (isPage) {
    return (
      <div className="container mt-4 mb-5">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="card">
              <div className="card-header bg-warning">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">🍪 Cookie Policy</h3>
                  <Button variant="outline-dark" onClick={handleClose}>
                    ← Back
                  </Button>
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {content}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>🍪 Cookie Policy</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {content}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CookiePolicy;

